export type FillTargetNotEditableState = 'readonly' | 'disabled' | 'not-fillable';

export interface FillTargetNotEditableErrorContext {
  ref: string;
  elementDesc?: string;
  state: FillTargetNotEditableState;
  tagName: string;
  role?: string | null;
  inputType?: string | null;
}

function labelForContext(context: FillTargetNotEditableErrorContext): string {
  return context.elementDesc ? `${context.elementDesc} (${context.ref})` : context.ref;
}

function elementDescription(context: FillTargetNotEditableErrorContext): string {
  const role = context.role ? ` role=${context.role}` : '';
  const type = context.inputType ? ` type=${context.inputType}` : '';
  return `${context.tagName}${type}${role}`;
}

export function formatFillTargetNotEditableMessage(
  context: FillTargetNotEditableErrorContext
): string {
  if (context.state === 'not-fillable') {
    return `Cannot fill target ${labelForContext(context)}: ${elementDescription(context)} cannot accept text.`;
  }

  return `Cannot fill ${context.state} field ${labelForContext(context)}: ${elementDescription(context)} is not editable.`;
}

export class FillTargetNotEditableError extends Error {
  readonly fillTarget: FillTargetNotEditableErrorContext;

  constructor(context: FillTargetNotEditableErrorContext) {
    super(formatFillTargetNotEditableMessage(context));
    this.name = 'FillTargetNotEditableError';
    this.fillTarget = context;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isFillTargetNotEditableError(
  error: unknown
): error is FillTargetNotEditableError {
  if (error instanceof FillTargetNotEditableError) return true;
  if (!isRecord(error) || error.name !== 'FillTargetNotEditableError') return false;
  if (!isRecord(error.fillTarget)) return false;

  const context = error.fillTarget;
  return (
    typeof context.ref === 'string' &&
    typeof context.tagName === 'string' &&
    (context.state === 'readonly' ||
      context.state === 'disabled' ||
      context.state === 'not-fillable')
  );
}

export function formatFillTargetNotEditableRecovery(
  error: FillTargetNotEditableError,
  toolName: 'browser_fill_form' | 'browser_type' = 'browser_fill_form'
): string {
  const context = error.fillTarget;
  const action = toolName === 'browser_type' ? 'type into' : 'fill';

  if (context.state === 'not-fillable') {
    return [
      `Error: ${toolName} cannot ${action} target ${labelForContext(context)}: ${elementDescription(context)} cannot accept typed text.`,
      `Do not retry ${toolName} for this ref. Call browser_snapshot and choose the actual editable input or textarea. If the target is a dropdown, use browser_select_option or click the dropdown and select an option.`,
    ].join(' ');
  }

  return [
    `Error: ${toolName} cannot ${action} ${context.state} field ${labelForContext(context)}: ${elementDescription(context)} is not editable.`,
    `Do not retry ${toolName} for this ref. Call browser_snapshot and choose an editable input or textarea for the same requirement, or complete the prerequisite that unlocks this field first.`,
  ].join(' ');
}
